import type { TestId } from '@repo/test-id';
import { Map, type ParkMarker } from '@repo/map';
import { useMarkerSelection } from '@repo/map';
import { useEffect, useMemo, useRef } from 'react';
import { usePartialParkings } from '@repo/domain-service';
import { useGeoPosition } from '@repo/geo-position';
import type { GeoBounds, GeoCoordinates, PartialParkingFilter } from '@repo/domain';
import { ConsentBlocker, usePrivacyConsent } from '@repo/privacy-consent';
import { tr } from '@repo/locale';
import { useMatch, useNavigate } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import MapLoadingBadge from '@features/parkingMap/components/MapLoadingBadge';
import { PARKING_SHAPES_MIN_ZOOM } from '@features/parkingMap/constants/parkingShapes';
import { createParkingMarkers } from '@features/parkingMap/utils/createParkingMarkers';
import { createParkingShapes } from '@features/parkingMap/utils/createParkingShapes';

import { useThemeContext } from '@providers/ThemeProvider';

import { useMapTypeStore } from '@shared/stores/useMapTypeStore';
import { useParkingSearchStore } from '@shared/stores/useParkingSearchStore';
import { getConfig } from '@shared/env';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

interface Props {
  testId: TestId;
}

const PARKING_MAP_NODE_ID = 'parkapp-map';
const DEBOUNCED_PARKING_SEARCH_MILLISECONDS = 500;
// Bottom is higher due to Scan QR code button.
const MAP_SAFE_AREA_INSETS = { top: 40, right: 40, bottom: 95, left: 40 } as const;
// A click on a parking zooms the map in to this level while its borders are not drawn yet.
const PARKING_CLICK_ZOOM = 18;

export default function Provider({ testId }: Props) {
  const hasGoogleMapsConsent = usePrivacyConsent('googleMaps');
  const { isDark } = useThemeContext();
  const navigate = useNavigate();
  const filters = useParkingSearchStore((state) => state.filters);
  const mapBounds = useParkingSearchStore((state) => state.mapBounds);
  const mapZoom = useParkingSearchStore((state) => state.mapZoom);
  const withShapes = mapZoom !== undefined && mapZoom >= PARKING_SHAPES_MIN_ZOOM;
  const setMapCamera = useParkingSearchStore((state) => state.setMapCamera);
  const bumpMapMoveNonce = useParkingSearchStore((state) => state.bumpMapMoveNonce);
  const parkingFilter = useMemo<PartialParkingFilter>(
    () => ({
      properties: filters,
      bounds: mapBounds,
      withShapes,
    }),
    [filters, mapBounds, withShapes]
  );
  const { partialParkings, fetchingPartialParkingsDelayed, partialParkingsTimedOut } =
    usePartialParkings(parkingFilter);
  const selectedParking = useParkingSearchStore((state) => state.selectedParking);
  const setSelectedParking = useParkingSearchStore((state) => state.setSelectedParking);
  const mapType = useMapTypeStore((state) => state.mapType);
  const { isKnown, position: geoPosition, repanFunction } = useGeoPosition();

  // Captures initial isKnown so we only pan on transitions from unknown → known.
  const wasKnownRef = useRef<boolean>(isKnown);
  const parkingDetailsRoute = ROUTE_PATHS[RouteName.PARKING_DETAILS];
  const isParkingDetailsRoute = Boolean(useMatch(parkingDetailsRoute));

  const markers = useMemo<ParkMarker[]>(() => createParkingMarkers(partialParkings), [partialParkings]);

  const { allMarkers, selectMarker } = useMarkerSelection({ markers });
  const parkingShapes = useMemo(
    () => createParkingShapes(partialParkings, selectedParking.slug),
    [partialParkings, selectedParking.slug]
  );

  // Auto-pan to user's position when location becomes newly known
  // (e.g., after granting permission via the location drawer).
  useEffect(() => {
    if (!isKnown || !geoPosition || !repanFunction || wasKnownRef.current) return;
    wasKnownRef.current = true;
    repanFunction(geoPosition);
  }, [isKnown, geoPosition, repanFunction]);

  const lastPannedParkingRef = useRef<string | undefined>(undefined);
  // Prevents the debounced onMapPan handler from triggering a new search
  // when the map is programmatically re-panned to a selected parking.
  const suppressSearchRef = useRef<boolean>(false);

  useEffect(() => {
    if (!selectedParking.slug) {
      lastPannedParkingRef.current = undefined;
      return;
    }

    selectMarker(selectedParking.slug);

    if (selectedParking.slug === lastPannedParkingRef.current) return;

    const parking = partialParkings.find((parking) => parking.slug === selectedParking.slug);
    if (!parking || !parking.address.coordinates) return;

    lastPannedParkingRef.current = selectedParking.slug;

    if (repanFunction) {
      suppressSearchRef.current = true;
      repanFunction(parking.address.coordinates);
    }
  }, [selectedParking.slug, selectMarker, partialParkings, repanFunction]);

  const handleMapPan = useDebouncedCallback(
    ({ bounds, center, zoom }: { bounds: GeoBounds; center: GeoCoordinates; zoom: number }): void => {
      if (suppressSearchRef.current) {
        suppressSearchRef.current = false;
        return;
      }
      setMapCamera({ center, zoom, bounds });
    },
    DEBOUNCED_PARKING_SEARCH_MILLISECONDS
  );

  const handleParkingClick = (parkingSlug: string): void => {
    const parking = partialParkings.find((parking) => parking.slug === parkingSlug);
    if (!parking) return;

    selectMarker(parking.slug);
    setSelectedParking(parking.id, parking.slug);
    if (!withShapes && parking.address.coordinates && repanFunction) {
      // Marked as panned so the selection effect neither re-pans nor suppresses the search the new zoom needs.
      lastPannedParkingRef.current = parking.slug;
      repanFunction(parking.address.coordinates, PARKING_CLICK_ZOOM);
    }
    if (!isParkingDetailsRoute) {
      navigate(parkingDetailsRoute);
    }
  };

  const handleParkingShapeClick = (key: string): void => {
    const parkingSlug = parkingShapes.parkingSlugByKey.get(key);
    if (parkingSlug) handleParkingClick(parkingSlug);
  };

  if (!hasGoogleMapsConsent) {
    return (
      <ConsentBlocker
        service="googleMaps"
        title={tr('map:google_maps_consent.title')}
        testId={testId.append('consent')}
      />
    );
  }

  return (
    <div className="relative h-full w-full">
      <Map
        mapNodeId={PARKING_MAP_NODE_ID}
        googleMapsKey={getConfig('mapsKey')}
        googleMapsId={getConfig('mapsId')}
        isDark={isDark}
        testId={testId}
        mapType={mapType}
        allMarkers={allMarkers}
        areas={parkingShapes.areas}
        pins={parkingShapes.pins}
        safeAreaInsets={MAP_SAFE_AREA_INSETS}
        onParkingClick={handleParkingClick}
        onAreaClick={handleParkingShapeClick}
        onPinClick={handleParkingShapeClick}
        onMapPan={handleMapPan}
        onUserPanOrZoom={bumpMapMoveNonce}
      />
      <MapLoadingBadge
        testId={testId}
        isLoadingDelayed={fetchingPartialParkingsDelayed}
        hasTimedOut={partialParkingsTimedOut}
      />
    </div>
  );
}
